import * as React from "react";
// Import interfaces
import { GridCell } from "../helpers/interfaces";
// Import types
import { ChannelWithPosition, GridEvent, Position } from "../helpers/types";
// Import helpers
import { generateArray } from "../helpers";
// Import styles
import { GridStyled } from "../styles";
import { GridDividerItem } from "./GridDividerItem";
import { useGridExternalDnD } from "../hooks/useGridExternalDnD";

const { GridItem } = GridStyled;

interface GridCellItemProps {
  isVerticalMode: boolean;
  isHoverHighlight: boolean | undefined;
  isProgramVisible: (position: Position, overlapsCount: number) => boolean;
  isItemClickable: boolean;
  isDayMode: boolean;
  item: { position: Position; channel: ChannelWithPosition };
  index: number;
  channelOverlapsCount: Record<string, number>;
  hourWidth: number;
  timelineDividers: number;
  renderGridCell?: (props: GridCell) => React.ReactNode;
  onItemClick: GridEvent;
  onItemDrop: GridEvent;
}

export function GridCellItem({
  isVerticalMode,
  isProgramVisible,
  isHoverHighlight,
  isDayMode,
  isItemClickable,
  item,
  index,
  channelOverlapsCount,
  hourWidth,
  timelineDividers,
  renderGridCell,
  onItemClick,
  onItemDrop,
}: GridCellItemProps) {
  const overlapsCount = channelOverlapsCount[item.channel.uuid] ?? 1;
  const isVisible = isProgramVisible(item.position, overlapsCount);

  const { isDragOver, dropAreaRef, onDrop, onDragEnter, onDragLeave } =
    useGridExternalDnD({
      onItemDrop,
    });

  if (!isVisible) return null;

  const gridItemClickProps = isDayMode
    ? { isItemClickable: false }
    : { onClick: onItemClick(item), isItemClickable };

  const dividerProps = {
    styles: {
      left: (index: number) => index * (hourWidth / timelineDividers),
      width: hourWidth / timelineDividers,
    },
    props: {
      isVerticalMode,
      isHoverHighlight: isHoverHighlight as boolean,
      isItemClickable,
      onItemClick,
      onItemDrop,
    },
  };

  const gridItemDnDProps = isDayMode
    ? {}
    : {
        isDragOver: isDragOver,
        onDragEnter,
        onDragLeave,
        onDrop: onDrop(item, index),
      };

  if (renderGridCell) {
    const id = item.channel.uuid + index;
    return renderGridCell({
      isDayMode,
      id,
      index,
      item,
      isHoverHighlight: isHoverHighlight as boolean,
      timelineDividers,
      gridDividerProps: dividerProps,
      timelineDividerArray: generateArray(timelineDividers),
      gridItemClickProps,
      gridItemDnDProps,
    });
  }

  return (
    <GridItem
      ref={dropAreaRef}
      key={item.channel.uuid + index}
      className="planby-grid-item"
      isDayMode={isDayMode}
      isHoverHighlight={isHoverHighlight as boolean}
      {...item.position}
      {...gridItemClickProps}
      {...gridItemDnDProps}
    >
      {isDayMode &&
        generateArray(timelineDividers).map((_, index) => (
          <GridDividerItem
            key={index}
            index={index}
            item={item}
            dividerProps={dividerProps}
          />
        ))}
    </GridItem>
  );
}
